// Helpers for WhatsApp Authentication templates built from Meta's template library
export const AUTH_TEMPLATE = "authentication-template";
export const CODE_EXPIRY_OPTIONS = [1, 2, 3, 5, 10, 15];
export const MAX_APPS = 5;
export const PACKAGE_NAME_MAX = 224;
export const SIGNATURE_HASH_LENGTH = 11;
export const SECURITY_LINE = "For your security, do not share this code.";
export const AUTH_NAME_ERROR = "Use lowercase letters, numbers and underscores only.";

const VARIABLE_REGEX = /\{\{\s*([^{}]+?)\s*\}\}/g;
const PACKAGE_NAME_REGEX = /^[a-zA-Z][a-zA-Z0-9_]*(\.[a-zA-Z][a-zA-Z0-9_]*)+$/;

// Meta can only create library formats whose variables run {{1}}, {{2}}, ... in order, each once.
// Others (e.g. verify_account: {{1}} {{5}} {{3}} {{4}} {{5}}) fail with "BODY is missing expected field(s) (example)".
const hasSequentialVariables = (body) => {
  const numbers = [...(body || "").matchAll(/\{\{\s*(\d+)\s*\}\}/g)].map((m) => Number(m[1]));
  return numbers.every((n, index) => n === index + 1);
};

// Library API response -> AUTHENTICATION entries Meta can actually create
export const normalizeLibrary = (data) => {
  const list = Array.isArray(data) ? data : data?.templates || data?.data || [];
  return list.filter(
    (t) => (t.category || "").toUpperCase() === "AUTHENTICATION" && hasSequentialVariables(t.body),
  );
};

// One entry per variable occurrence, in body order
export const getBodyVariables = (body) =>
  [...(body || "").matchAll(VARIABLE_REGEX)].map((match) => match[1]);

// One variable always starts mapped to OTP (the user can change it); the rest start unmapped.
// OTP pick: {{code}}, else the variable Meta types as CODE, else one whose example looks like a code, else the first.
// Meta's paramTypes/params are indexed by placeholder number: {{n}} -> index n - 1.
export const buildSampleFields = (body, paramTypes = [], params = []) => {
  const names = getBodyVariables(body);
  const metaIndex = (name) => (/^\d+$/.test(name) ? Number(name) - 1 : -1);
  const pickers = [
    (name) => name === "code",
    (name) => paramTypes?.[metaIndex(name)] === "CODE",
    (name) => /^\d{4,8}$/.test(params?.[metaIndex(name)] || ""),
  ];
  const picked = pickers.map((test) => names.findIndex(test)).find((index) => index !== -1);
  const otpIndex = picked ?? 0;

  return names.map((name, index) =>
    index === otpIndex
      ? { type: "otp", value: params?.[metaIndex(name)] || "", variable_name: name }
      : { type: "", value: "", variable_name: name },
  );
};

export const getAppErrors = (apps) =>
  apps.map((app, index) => {
    const errors = {};
    const packageName = app.package_name.trim();
    if (
      !packageName ||
      packageName.length > PACKAGE_NAME_MAX ||
      !PACKAGE_NAME_REGEX.test(packageName)
    ) {
      errors.package_name = "Enter package names like com.example.myapplication.";
    } else if (apps.findIndex((a) => a.package_name.trim() === packageName) !== index) {
      errors.package_name = "This package name is already added.";
    }
    if (app.signature_hash.length !== SIGNATURE_HASH_LENGTH) {
      errors.signature_hash = `The signature hash must be ${SIGNATURE_HASH_LENGTH} characters long.`;
    }
    return errors;
  });

// Labels for the "To submit, add: ..." hint; empty means the form is valid
export const getAuthMissing = (auth, templateName, nameError) => {
  const missing = [];
  if (!templateName.trim() || nameError) missing.push("template name");
  if (!auth?.selected) return [...missing, "template"];

  const fields = auth.sampleBodyFields;
  if (fields.some((f) => !f.type)) missing.push("variable mapping");
  if (fields.some((f) => (f.type === "otp" || f.type === "custom") && !f.value?.trim())) {
    missing.push("sample data");
  }
  if (fields.length > 0 && fields.filter((f) => f.type === "otp").length !== 1) {
    missing.push("OTP variable");
  }
  if (auth.otpType === "ZERO_TAP" && !auth.termsAccepted) missing.push("zero-tap terms");
  if (
    auth.otpType !== "COPY_CODE" &&
    getAppErrors(auth.apps).some((errors) => Object.keys(errors).length > 0)
  ) {
    missing.push("app details");
  }
  return missing;
};

export const buildPreviewBody = (body, sampleBodyFields, addSecurity) => {
  let index = 0;
  const text = (body || "").replace(VARIABLE_REGEX, (match) => {
    const field = sampleBodyFields[index++];
    if (!field?.type) return match;
    if (field.type === "otp" || field.type === "custom") return field.value?.trim() || match;
    return `{{${field.type}}}`;
  });
  return addSecurity ? `${text} ${SECURITY_LINE}` : text;
};

export const buildExpiryFooter = (minutes) =>
  `This code expires in ${minutes} minute${minutes === 1 ? "" : "s"}.`;

export const otpButtonText = (otpType) => (otpType === "COPY_CODE" ? "Copy code" : "Autofill");

// Body, footer and OTP button for the phone preview (create page and template list)
export const buildAuthPreview = ({ body, sampleBodyFields, addSecurity, expiryMinutes, otpType }) => ({
  body: buildPreviewBody(body, sampleBodyFields || [], addSecurity),
  footer: expiryMinutes ? buildExpiryFooter(expiryMinutes) : "",
  allButtons: [
    {
      type: otpType === "COPY_CODE" ? "copy-code" : "autofill",
      data: { buttonText: otpButtonText(otpType) },
    },
  ],
});

// Preview overrides for a stored authentication template ({} for other templates).
// Values are already filled into the body, so sampleBodyFields is emptied to skip the generic {{n}} replacement.
export const authPreviewProps = (previewJson) =>
  previewJson?.marketingOption === "template-library"
    ? {
        ...buildAuthPreview({
          body: previewJson.body,
          sampleBodyFields: previewJson.sampleBodyFields,
          addSecurity: String(previewJson.addSecurityRecommendation) === "true",
          expiryMinutes: Number(previewJson.codeExpirationMinutes) || 0,
          otpType: previewJson.otpType,
        }),
        sampleBodyFields: [],
      }
    : {};

// Extra multipart fields for the create endpoint
export const buildAuthPayload = (auth) => {
  const payload = {
    marketingOption: "template-library",
    libraryTemplateName: auth.selected.name,
    body: auth.selected.body || "",
    sampleBodyFields: auth.sampleBodyFields.length
      ? JSON.stringify(
          auth.sampleBodyFields.map((f) => ({ ...f, is_otp: f.type === "otp" })),
        )
      : "",
    addSecurityRecommendation: auth.addSecurity,
    otpType: auth.otpType,
  };
  if (auth.expiryEnabled) payload.codeExpirationMinutes = auth.expiryMinutes;
  if (auth.otpType === "ZERO_TAP") payload.zeroTapTermsAccepted = auth.termsAccepted;
  if (auth.otpType !== "COPY_CODE") {
    payload.supportedApps = JSON.stringify(
      auth.apps.map((app) => ({
        package_name: app.package_name.trim(),
        signature_hash: app.signature_hash,
      })),
    );
  }
  return payload;
};
